import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Skeleton } from "@ui/skeleton/Skeleton"
import { Typo } from "@ui/text/Typo"
import { PositiveSizeChart } from "@ui/chart/PositiveSizeChart"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { GD } from "src/GD"
import { PRIVATE_MOBILE_BREAKPOINT as MOBILE, PRIVATE_NARROW_BREAKPOINT as NARROW, PRIVATE_THEME as T } from "../theme"
import type { PrivateTab } from "../PrivateView"
import { getFriendlyErrorMessage } from "src/utils/errors"
import { formatPrivateMoney } from "../money"
import { Callout, IconShield, PageIntro, PrivateSurface } from "../ui"
import { DS, HeroButton, HeroStat, StatGroup } from "../../ds/Kit"
import { TransactionDetails } from "../../components/TransactionDetails"
import { getTransactionDisplayLabel } from "src/utils/transactionPresentation"
import { S_UNIVERSAL_DIALOG } from "../../components/universalDialog/signals"
import { useIssuer } from "../../issuer/IssuerProvider"
import {
	Area,
	AreaChart,
	CartesianGrid,
	Cell,
	Pie,
	PieChart,
	Tooltip,
	XAxis,
	YAxis
} from "recharts"

const fmt = (cents: number, currency?: string) => formatPrivateMoney(Number(cents || 0), currency)
const compactFmt = (cents: number, currency: string) => {
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency,
			currencyDisplay: "narrowSymbol",
			notation: "compact",
			maximumFractionDigits: 1
		}).format(Number(cents || 0) / CENTS_PER_CURRENCY_UNIT)
	} catch {
		return `${currency} ${Math.round(Number(cents || 0) / CENTS_PER_CURRENCY_UNIT)}`
	}
}
const CARD_CHART_COLORS = [T.accent, "#4E7AC7", "#D68B39", "#8B6CCF", "#D45C78"]
const CENTS_PER_CURRENCY_UNIT = 100
const PERCENT = 100
const RECENT_ACTIVITY_LIMIT = 5
const LAST_FOUR_DIGITS = -4
const LAST_DATE_CHARS = 10

const shortDate = (value: string) => {
	const date = new Date(`${value}T00:00:00`)
	return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}

export const PrivateDashboard = ({ onNavigate }: { onNavigate: (t: PrivateTab, cardUid?: string) => void }) => {
	const { selectedIssuer } = useIssuer()
	const currency = selectedIssuer?.currency ?? "USD"
	const [overview, setOverview] = useState<IStatsOverviewStats | null>(null)
	const [cards, setCards] = useState<ICardSaved[]>([])
	const [cardsStats, setCardsStats] = useState<IStatsByCardStats | null>(null)
	const [transactionCount, setTransactionCount] = useState(0)
	const [recentTransactions, setRecentTransactions] = useState<ICompanyTrxTransaction[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [reloadKey, setReloadKey] = useState(0)
	const [balanceVisible, setBalanceVisible] = useState(true)
	const verificationRequired = error === "Complete identity verification to unlock account activity and card features."

	useEffect(() => {
		let alive = true
		;(async () => {
			setLoading(true)
			setError(null)
			setOverview(null)
			setCards([])
			setCardsStats(null)
			setTransactionCount(0)
			setRecentTransactions([])
			try {
				const [statsRes, cardsRes, cardsStatsRes, transactionsRes] = await Promise.all([
					GD.REQ_STATS_OVERVIEW.request({
						days: 30,
						currency,
						entity: "card",
						tx_type: "spent"
					}),
					GD.REQ_CARDS.request(),
					GD.REQ_STATS_BY_CARD.request({ days: 30, currency, tx_type: "spent" }),
					GD.REQ_COMPANY_TRANSACTIONS_ALL.request({ page: 0, limit: RECENT_ACTIVITY_LIMIT })
				])
				if (!alive) return
				const requestError = statsRes.error ?? cardsRes.error ?? cardsStatsRes.error ?? transactionsRes.error
				if (requestError) setError(getFriendlyErrorMessage(requestError, "Could not load account overview"))
				if (statsRes.data) setOverview(statsRes.data)
				if (cardsRes.data) setCards(cardsRes.data)
				if (cardsStatsRes.data) setCardsStats(cardsStatsRes.data)
				if (transactionsRes.data) {
					setTransactionCount(transactionsRes.data.pagination.total)
					setRecentTransactions(transactionsRes.data.transactions ?? [])
				}
			} catch (cause) {
				if (alive) setError(getFriendlyErrorMessage(cause, "Could not load account overview"))
			} finally {
				if (alive) setLoading(false)
			}
		})()
		return () => {
			alive = false
		}
	}, [currency, reloadKey])

	const cardsTotal = cardsStats?.cards_total ?? cards.length
	const activeCards = cardsStats?.cards_active ?? cards.filter(c => c.status === "active").length
	const cardsBalance = cards.reduce((acc, c) => acc + (c.balance || 0), 0)

	const dailyAverage = useMemo(() => {
		const daily = overview?.daily ?? []
		return daily.length ? Math.round(daily.reduce((sum, day) => sum + day.amount_spent, 0) / daily.length) : 0
	}, [overview])
	const peakDay = useMemo(() => {
		const daily = overview?.daily ?? []
		return daily.reduce<IStatsOverviewPoint | null>((peak, day) =>
			day.amount_spent > 0 && (!peak || day.amount_spent > peak.amount_spent) ? day : peak,
		null)
	}, [overview])
	const cardSpending = useMemo(() => (cardsStats?.cards ?? [])
		.map((card, index) => ({
			name: card.card_name || (card.card_mask ? `Card •${card.card_mask.slice(LAST_FOUR_DIGITS)}` : `Card ${index + 1}`),
			value: Math.max(0, Number(card.amount) || 0),
			color: CARD_CHART_COLORS[index % CARD_CHART_COLORS.length]
		}))
		.filter(card => card.value > 0), [cardsStats])
	const totalCardSpend = cardsStats?.total ?? 0

	const periodSpent = fmt(overview?.totals.amount_spent ?? 0, currency)

	return (
		<VBox gap="16px">
			<PageIntro
				title="Financial overview"
				description="See your balance, cards and recent transactions."
			/>
			{error && (verificationRequired ? (
				<VerificationNotice />
			) : (
				<Callout
					role="alert"
					tone="error"
					title="Could not load account overview"
					action={<Button variant="secondary" onClick={() => setReloadKey(key => key + 1)}>Retry</Button>}
				>
					{error}
				</Callout>
			))}

			{/* Balance hero */}
			<Box role="region" aria-label="Balance and quick actions">
				<HeroStat
					label="Available balance"
					value={
						<HBox gap="10px" sx={{ alignItems: "center" }}>
							<span>{balanceVisible ? fmt(Number(selectedIssuer?.balance ?? 0), currency) : "••••••"}</span>
							<Box
								element="button"
								type="button"
								aria-label={balanceVisible ? "Hide balance" : "Show balance"}
								onClick={() => setBalanceVisible(visible => !visible)}
								sx={{ display: "flex", padding: "7px", border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.9)", cursor: "pointer", borderRadius: "10px", "&:hover": { background: "rgba(255,255,255,.2)" }, "&:focus-visible": { outline: "2px solid #fff" } }}>
								<IconEye crossed={!balanceVisible} />
							</Box>
						</HBox>
					}
					hint={`${selectedIssuer?.currency ?? "—"} · ${selectedIssuer?.name ?? "No issuer"}`}
					actions={
						<>
							{selectedIssuer?.crypto_topup && <HeroButton onClick={() => onNavigate("funding")}>Add funds</HeroButton>}
							<HeroButton ghost onClick={() => onNavigate("cards")}>Manage cards</HeroButton>
							<HeroButton ghost onClick={() => onNavigate("transactions")}>View transactions</HeroButton>
						</>
					}
				/>
			</Box>

			{!error && (
				<StatGroup
					items={[
						{ label: "Spent · 30 days", value: loading ? "—" : periodSpent },
						{ label: "Transactions", value: loading ? "—" : String(transactionCount) },
						{ label: "Active cards", value: loading ? "—" : `${activeCards} / ${cardsTotal}` },
						{ label: "Across cards", value: loading ? "—" : fmt(cardsBalance, currency) }
					]}
				/>
			)}

			{/* Recharts derives the scale directly from the daily series. */}
			{loading && !error && <Box role='status' aria-label='Loading account activity'><PrivateSurface sx={{ padding: "16px" }}>
				<Skeleton mode="manual" busy shape="rect" width="100%" height="230px" sx={{ borderRadius: "16px" }} />
			</PrivateSurface></Box>}
			{!error && !loading && <>
				<Panel
					title="Spending activity"
					subtitle="Last 30 days"
					action={<VBox sx={{ alignItems: "flex-end" }}><Typo sx={{ color: DS.muted, fontSize: "12px" }}>Total spent</Typo><Typo sx={{ color: DS.ink, fontSize: "16px", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{periodSpent}</Typo></VBox>}
				>
					{!overview?.daily?.length ? (
						<Box sx={{ height: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}><Typo sx={{ color: DS.muted }}>No spending data for this period.</Typo></Box>
					) : (
						<Box sx={{ width: "100%", minWidth: 0, minHeight: 0, height: "230px", [MOBILE]: { height: "205px", marginLeft: "-8px", width: "calc(100% + 8px)" } }}>
							<PositiveSizeChart>
								<AreaChart data={overview.daily} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
									<defs>
										<linearGradient id="privateSpendGradient" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor={T.accent} stopOpacity={0.28} />
											<stop offset="100%" stopColor={T.accent} stopOpacity={0.01} />
										</linearGradient>
									</defs>
									<CartesianGrid vertical={false} stroke={DS.line} strokeDasharray="4 6" />
									<XAxis dataKey="date" axisLine={false} tickLine={false} minTickGap={34} tickFormatter={shortDate} tick={{ fill: DS.muted, fontSize: 11, fontWeight: 500 }} dy={9} />
									<YAxis axisLine={false} tickLine={false} width={52} tick={{ fill: DS.muted, fontSize: 11 }} tickFormatter={value => compactFmt(Number(value), currency)} />
									<Tooltip
										contentStyle={{ borderRadius: "10px", border: "none", backgroundColor: "#111827", boxShadow: "0 10px 15px -3px rgba(0,0,0,.1)", padding: "8px 12px" }}
										itemStyle={{ color: "#38bdf8", fontSize: "12px", fontWeight: 600 }}
										labelStyle={{ color: "#94a3b8", fontSize: "11px", marginBottom: "4px" }}
										formatter={(value: number | string | undefined) => [fmt(Number(value) || 0, currency), "Spent"]}
										labelFormatter={label => shortDate(String(label))}
									/>
									<Area type="monotone" dataKey="amount_spent" name="Spent" stroke={T.accent} strokeWidth={2.5} fill="url(#privateSpendGradient)" activeDot={{ r: 5, fill: T.accent, stroke: "#fff", strokeWidth: 2 }} />
								</AreaChart>
							</PositiveSizeChart>
						</Box>
					)}
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px", marginTop: "4px", [NARROW]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
						<ChartInsight label="Daily average" value={fmt(dailyAverage, currency)} detail="Across the visible period" />
						<ChartInsight label="Peak spending" value={fmt(peakDay?.amount_spent ?? 0, currency)} detail={peakDay ? shortDate(peakDay.date) : "No peak yet"} />
						<ChartInsight label="Card readiness" value={cardsTotal ? `${Math.round((activeCards / cardsTotal) * PERCENT)}%` : "—"} detail={`${activeCards} of ${cardsTotal} cards active`} />
					</Box>
				</Panel>

				<Box sx={{ display: "grid", gridTemplateColumns: "minmax(300px, .82fr) minmax(0, 1.18fr)", gap: "16px", "@media (max-width: 940px)": { gridTemplateColumns: "minmax(0, 1fr)" } }}>
					<Panel
						title="Spending by card"
						subtitle="Last 30 days across all cards"
						action={<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{fmt(totalCardSpend, currency)}</Typo>}
					>
						{cardSpending.length ? (
							<Box sx={{ display: "grid", gridTemplateColumns: "minmax(150px, .9fr) minmax(0, 1.1fr)", alignItems: "center", gap: "12px", [NARROW]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
								<Box sx={{ position: "relative", width: "100%", minWidth: 0, minHeight: 0, height: "190px" }}>
									<PositiveSizeChart><PieChart><Pie data={cardSpending} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} stroke="none">{cardSpending.map(card => <Cell key={card.name} fill={card.color} />)}</Pie><Tooltip contentStyle={{ borderRadius: "10px", border: "none", backgroundColor: "#111827", fontSize: "12px" }} itemStyle={{ color: "#38bdf8" }} formatter={(value: number | string | undefined) => [fmt(Number(value) || 0, currency), "Spent"]} /></PieChart></PositiveSizeChart>
									<VBox sx={{ position: "absolute", inset: 0, alignItems: "center", justifyContent: "center", pointerEvents: "none" }}><Typo sx={{ color: DS.muted, fontSize: "12px" }}>Spent</Typo><Typo sx={{ color: DS.ink, fontSize: "16px", fontWeight: 700 }}>{fmt(totalCardSpend, currency)}</Typo></VBox>
								</Box>
								<VBox gap="12px">{cardSpending.slice(0, RECENT_ACTIVITY_LIMIT).map(card => <HBox key={card.name} sx={{ alignItems: "center", justifyContent: "space-between", gap: "12px" }}><HBox gap="9px" sx={{ alignItems: "center", minWidth: 0 }}><Box aria-hidden sx={{ width: "9px", height: "9px", flexShrink: 0, borderRadius: "50%", backgroundColor: card.color }} /><Typo sx={{ color: DS.text, fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{card.name}</Typo></HBox><VBox sx={{ alignItems: "flex-end", flexShrink: 0 }}><Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{fmt(card.value, currency)}</Typo><Typo sx={{ color: DS.muted, fontSize: "12px" }}>{totalCardSpend ? `${Math.round((card.value / totalCardSpend) * PERCENT)}%` : "0%"}</Typo></VBox></HBox>)}</VBox>
							</Box>
						) : <Box sx={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}><Typo sx={{ color: DS.muted }}>No card spending for this period.</Typo></Box>}
					</Panel>

					<Panel
						title="Transaction activity"
						subtitle="Latest incoming and outgoing transactions"
						action={<Button variant="secondary" onClick={() => onNavigate("transactions")} sx={{ minHeight: "36px", padding: "0 14px", borderRadius: "10px", fontSize: "13px" }}>View all</Button>}
					>
						{recentTransactions.length ? <VBox>{recentTransactions.slice(0, RECENT_ACTIVITY_LIMIT).map(transaction => <RecentActivityRow key={transaction.id} transaction={transaction} onTransactions={uid => onNavigate("transactions", uid)} />)}</VBox> : <Box sx={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}><Typo sx={{ color: DS.muted }}>No recent transactions.</Typo></Box>}
					</Panel>
				</Box>
			</>}
		</VBox>
	)
}

const Panel = ({ title, subtitle, action, children }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode }) => (
	<VBox gap="14px" sx={{ padding: "18px 20px 20px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, backgroundColor: DS.surface, boxShadow: DS.shadow, minWidth: 0 }}>
		<HBox sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
			<VBox gap="2px" sx={{ minWidth: 0 }}>
				<Typo sx={{ fontSize: "16px", fontWeight: 650, color: DS.ink }}>{title}</Typo>
				{subtitle && <Typo sx={{ fontSize: "13px", color: DS.muted }}>{subtitle}</Typo>}
			</VBox>
			{action}
		</HBox>
		{children}
	</VBox>
)

const VerificationNotice = () => (
	<Callout
		role="status"
		ariaLabel="Identity verification required"
		tone="warning"
		icon={<IconShield />}
		title="Complete verification"
	>
		Finish the remaining check to unlock account activity and card features.
	</Callout>
)

const ChartInsight = ({ label, value, detail }: { label: string; value: string; detail: string }) => (
	<Box sx={{ padding: "12px 14px", borderRadius: "14px", backgroundColor: DS.soft, border: `1px solid ${DS.line}` }}>
		<Typo sx={{ color: DS.muted, fontSize: "12px", fontWeight: 500 }}>{label}</Typo>
		<Typo sx={{ color: DS.ink, fontSize: "16px", fontWeight: 700, letterSpacing: "-0.02em", marginTop: "4px", fontVariantNumeric: "tabular-nums" }}>{value}</Typo>
		<Typo sx={{ color: DS.muted, fontSize: "12px", marginTop: "2px" }}>{detail}</Typo>
	</Box>
)

const RecentActivityRow = ({ transaction, onTransactions }: { transaction: ICompanyTrxTransaction; onTransactions: (cardUid: string) => void }) => {
	const income = transaction.direction === "income"
	const displayLabel = getTransactionDisplayLabel(transaction, undefined, true)
	const openTransactionDetails = () => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Transaction Details",
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
			sx: { maxWidth: "600px", width: "100%" },
			content: <TransactionDetails transaction={transaction} onTransactions={onTransactions} privateCardActionsOnly />
		})
	}
	return (
		<HBox
			element="button"
			type="button"
			aria-label={`Open transaction details: ${displayLabel || transaction.id}`}
			onClick={openTransactionDetails}
			sx={{
				width: "100%",
				alignItems: "center",
				justifyContent: "space-between",
				gap: "14px",
				padding: "12px 10px",
				margin: "0 -10px",
				border: "none",
				borderTop: `1px solid ${DS.line}`,
				borderRadius: "12px",
				backgroundColor: "transparent",
				font: "inherit",
				textAlign: "left",
				cursor: "pointer",
				transition: "background-color .15s ease",
				"&:first-of-type": { borderTop: 0 },
				"&:hover": { backgroundColor: DS.soft },
				"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-2px" },
				[NARROW]: { alignItems: "flex-start", gap: "10px" }
			}}
		>
			<HBox gap="12px" sx={{ alignItems: "center", minWidth: 0 }}>
				<Box aria-hidden sx={{ width: "38px", height: "38px", flexShrink: 0, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: income ? "#047857" : "#B42318", backgroundColor: income ? "#ECFDF5" : "#FEF2F2" }}>
					<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={income ? "M17 7 7 17M7 17h8M7 17V9" : "M7 17 17 7M17 7H9M17 7v8"} /></svg>
				</Box>
				<VBox gap="2px" sx={{ minWidth: 0 }}>
					<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayLabel || (income ? "Funds in" : "Payment")}</Typo>
					<Typo sx={{ color: DS.muted, fontSize: "12px" }}>{shortDate(transaction.created_at.slice(0, LAST_DATE_CHARS))}{transaction.card_mask ? ` · •${transaction.card_mask.slice(LAST_FOUR_DIGITS)}` : ""}</Typo>
				</VBox>
			</HBox>
			<VBox gap="2px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
				<Typo sx={{ color: income ? "#047857" : DS.ink, fontSize: "14px", fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{income ? "+" : "−"}{fmt(transaction.amount, transaction.currency)}</Typo>
				<Typo sx={{ color: transaction.status === "completed" ? T.success : T.warning, fontSize: "12px", textTransform: "capitalize" }}>{transaction.status}</Typo>
			</VBox>
		</HBox>
	)
}

const IconEye = ({ crossed }: { crossed: boolean }) => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<path d="M2.8 12s3.3-5.4 9.2-5.4 9.2 5.4 9.2 5.4-3.3 5.4-9.2 5.4S2.8 12 2.8 12Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
		<circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
		{crossed ? <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /> : null}
	</svg>
)
